<template>
	<t-input-wrapper
		:label="label"
		:description="description"
		:hint="hint"
		:isInvalid="isInvalid"
		:validationMessage="validationMessage"
		:warningMessage="warningMessage"
	>
		<div :style="getStyle" class="text-input-button">
			<t-text-input
				ref="input"
				:value="value"
				:placeholder="placeholder"
				:disabled="disabled"
				:size="size"
				:isInvalid="isInvalid"
				:tabindex="disableTabbing ? -1 : 0"
				inputCssClass="text-input-with-button"
				v-on="inputWrapperListeners"
				@blur="onBlur"
			/>
			<t-icon-button
				ref="iconButton"
				:appearance="appearance"
				:intent="intent"
				:size="size"
				:icon="icon"
				:type="iconType"
				:iconSize="iconSize"
				:disabled="disabled || buttonIsOnlyVisual || buttonDisabled"
				:tabindex="(disableTabbing || disableButtonTabbing || buttonIsOnlyVisual) ? -1 : 0"
				:class="[{invalid: isInvalid}]"
				class="button textInputButton"
				@click="onClick"
				@mousedown="onMouseDown"
				@blur="onButtonBlur"
				@keydown="onButtonKeydown"
			/>
		</div>
	</t-input-wrapper>
</template>

<script>
import TTextInput from './TTextInput';
import TIconButton from '../buttons/TIconButton';
import formMixin from './formMixin';
import inputWrapperMixin from './inputWrapperMixin';
export default {
	name: 'TTextInputButton',
	components: { TTextInput, TIconButton },
	mixins: [ formMixin, inputWrapperMixin ],
	props: {
		appearance: String,
		intent: String,
		icon: String,
		iconSize: {
			type: String,
			default: 'sm'
		},
		buttonIsOnlyVisual: {
			type: Boolean,
			default: false
		},
		buttonDisabled: {
			type: Boolean,
			default: false
		},
		iconType: {
			type: String,
			default: 'bp'
		},
		disableTabbing: {
			type: Boolean,
			default: false
		},
		disableButtonTabbing: {
			type: Boolean,
			default: false
		},
	},
	methods: {
		onClick(event) {
			/**
			 * Event will be triggered after click on a button
			 * @event click
			 * @type {object}
			 */
			this.$emit('click', event);
			this.$emit('button:click', event);
		},
		onButtonKeydown(event) {
			this.$emit('button:keydown', event);
		},
		onMouseDown(event) {
			/**
			 * Event will be triggered after click on a button
			 * @event mousedown
			 * @type {object}
			 */
			this.$emit('mousedown', event);
		},
		onBlur(event) {
			/**
			 * Fired when input looses focus.
			 * @event blur
			 * @type {object}
			 */

			if (
				this.inputWrapperListeners
				&& this.inputWrapperListeners.blur
			)
				return; // Prevent duplicate event firing from v-on="inputWrapperListeners"

			if (this.$listeners && this.$listeners.blurring) {
				this.blurWithAbort();
				return;
			}

			this.$emit('blur', event);
		},
		onButtonBlur(event) {
			/**
			 * Fired when input looses focus.
			 * @event blur
			 * @type {object}
			 */
			if (this.$listeners && this.$listeners.blurring) {
				this.blurWithAbort();
				return;
			}

			this.$emit('button:blur', event);
		},
		async blurWithAbort() {

			let aborting
				= await new Promise( (resolve) => {
					this.$emit('blurring',
						(shouldAbort) => resolve(
							shouldAbort === undefined
								? true
								: Boolean(shouldAbort)
						), event);
				} );
			if (aborting)
				return;

			this.$emit('blur', event);
		},
		focus() {
			this.$refs.input.focus();
		},
		blur() {
			this.$refs.input.blur();
		}
	}
};
</script>

<style lang="scss">
	.text-input-with-button {
		border-radius: 3px 0px 0px 3px !important;
	}
</style>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.text-input-button {
	display: inline-flex;
	flex-direction: row;
	box-sizing: border-box;
}

.button {
	border-radius: 0px 3px 3px 0px;
	margin-left: -1px;
	&.invalid {
		box-shadow: inset 0 0 0 1px get-color($intent, 'danger'),
					inset 0 1px 2px get-color($neutral-scale, 'N4A');
	}
}
</style>


<docs>
## Examples

### Placeholder

```jsx
<t-text-input-button width="280px" placeholder="Text input placeholder..." icon="user"></t-text-input-button>
```

### Disabled

```jsx
<t-text-input-button width="280px" placeholder="Disabled" icon="user" disabled></t-text-input-button>
```
</docs>
